import { Button, CharCounter, Input, Select } from "@/components";
import {
  CheckCircle,
  ClipboardList,
  Database,
  Plus,
  ShieldCheck,
  Users,
  X,
  Loader2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import apiService from "@/shared/apiService";
import { useTranslation } from "@/contexts/LanguageContext";
import { useAds } from "../hooks/AdsContext";
import { useActiveContext } from "@/contexts/ActiveContextContext";
import {
  CUSTOM_QUESTION_LABEL_LIMIT,
  LINKEDIN_LEAD_FORM_LIMITS,
} from "../utils/charLimits";

const CTA_OPTIONS = [
  { value: "SIGN_UP", label: "Cadastrar" },
  { value: "LEARN_MORE", label: "Saiba Mais" },
  { value: "APPLY", label: "Candidatar-se" },
  { value: "DOWNLOAD", label: "Baixar" },
  { value: "GET_QUOTE", label: "Solicitar Orçamento" },
];

const Step4_LeadForm: React.FC = () => {
  const {
    leadFields,
    setLeadFields,
    customFields,
    setCustomFields,
    addCustomField,
    standardLeadFields,
    privacyPolicyUrl,
    setPrivacyPolicyUrl,
    platform,
    selectedLinkedinAccount,
    linkedinLeadFormUrn,
    setLinkedinLeadFormUrn,
    linkedinFormName,
    setLinkedinFormName,
    linkedinFormCta,
    setLinkedinFormCta,
    linkedinFormHeadline,
    setLinkedinFormHeadline,
    linkedinFormDescription,
    setLinkedinFormDescription,
  } = useAds();
  const { t } = useTranslation();

  const { activeContext } = useActiveContext();

  // O formulário nativo do LinkedIn não tem campo predefinido equivalente a
  // nenhum desses — ficam ocultos nessa plataforma pra não sugerir algo que
  // a API descartaria silenciosamente ao criar o formulário de verdade.
  const LINKEDIN_UNSUPPORTED_FIELDS = ["cpf", "budget", "industry", "website"];
  const visibleStandardLeadFields =
    platform === "linkedin"
      ? standardLeadFields.filter(
          (f: any) => !LINKEDIN_UNSUPPORTED_FIELDS.includes(f.id),
        )
      : standardLeadFields;

  // Campos ativos (padrão selecionados + personalizados preenchidos), na
  // ordem em que aparecem, pro preview genérico do formulário ao lado.
  const previewFields = [
    ...leadFields
      .map((id) => visibleStandardLeadFields.find((f: any) => f.id === id))
      .filter(Boolean),
    ...customFields.filter((f) => f.label.trim()),
  ] as { id: string; label: string; required: boolean }[];

  const [linkedinForms, setLinkedinForms] = useState<any[]>([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [loadingFormsError, setLoadingFormsError] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  // Auto-fill privacy policy URL from organization/clientCompany data
  useEffect(() => {
    if (!privacyPolicyUrl) {
      const auto =
        activeContext.clientCompany?.privacy_policy_url ||
        activeContext.organization?.privacy_policy_url ||
        "";
      if (auto) setPrivacyPolicyUrl(auto);
    }
  }, [activeContext]);

  const fetchLinkedinForms = React.useCallback(() => {
    if (platform === "linkedin" && selectedLinkedinAccount) {
      setLoadingForms(true);
      setLoadingFormsError(false);
      apiService
        .get(
          `/api/ads/linkedin/leadforms?account_urn=${selectedLinkedinAccount}`,
        )
        .then((res: any) => {
          if (res.success && res.elements) {
            setLinkedinForms(res.elements);
          } else {
            setLinkedinForms([]);
          }
        })
        .catch(() => {
          setLinkedinForms([]);
          setLoadingFormsError(true);
        })
        .finally(() => setLoadingForms(false));
    } else {
      setLinkedinForms([]);
    }
  }, [platform, selectedLinkedinAccount]);

  useEffect(() => {
    fetchLinkedinForms();
  }, [fetchLinkedinForms]);

  const handleCreateFormNow = async () => {
    if (!linkedinFormName.trim()) {
      setCreateError(t("Nome do formulário é obrigatório."));
      return;
    }
    if (!privacyPolicyUrl.trim()) {
      setCreateError(
        t("URL da política de privacidade é obrigatória (seção acima)."),
      );
      return;
    }

    setCreateError("");
    setCreating(true);

    const standardFields =
      leadFields.length > 0
        ? leadFields.map((f) => f.toUpperCase())
        : ["EMAIL", "FIRST_NAME", "LAST_NAME"];
    const customQuestions = customFields
      .filter((f) => f.label.trim())
      .map((f) => ({ label: f.label, required: f.required ?? false }));

    try {
      const res: any = await apiService.post("/api/ads/linkedin/leadforms", {
        account_urn: selectedLinkedinAccount,
        name: linkedinFormName.trim(),
        headline: linkedinFormHeadline.trim() || undefined,
        description: linkedinFormDescription.trim() || undefined,
        privacy_policy_url: privacyPolicyUrl.trim(),
        cta_type: linkedinFormCta,
        fields: standardFields,
        custom_questions:
          customQuestions.length > 0 ? customQuestions : undefined,
      });

      if (res.success) {
        const newForm = {
          urn: res.form_urn,
          name: linkedinFormName.trim(),
          status: "ACTIVE",
        };
        setLinkedinForms((prev) => [newForm, ...prev]);
        setLinkedinLeadFormUrn(res.form_urn);
      } else {
        setCreateError(res.error || t("Erro ao criar formulário."));
      }
    } catch (err: any) {
      setCreateError(err?.message || t("Erro de conexão ao criar formulário."));
    } finally {
      setCreating(false);
    }
  };

  const removeCustomField = (id: string) => {
    setCustomFields(customFields.filter((f) => f.id !== id));
  };

  const updateCustomField = (id: string, updates: any) => {
    setCustomFields(
      customFields.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    );
  };

  return (
    <div className="w-full space-y-4 animate-in fade-in slide-in-from-bottom-3 duration-400">
      {/* Lead Form Header & Fields */}
      <div className="bg-white border border-purple-200/70 rounded-2xl p-4 md:p-5 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b pb-3 border-purple-100">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-purple-600 rounded-lg text-white shadow-xs">
              <Users className="w-5 h-5" />
            </div>
            <div className="h-8 flex flex-col justify-between">
              <h2 className="text-xs font-bold text-gray-900 uppercase tracking-tight leading-none">
                {t("Campos de Captura de Leads")}
              </h2>
              <span className="text-[10px] text-gray-400 font-medium leading-none">
                {t("Defina as informações solicitadas no formulário nativo")}
              </span>
            </div>
          </div>
          <span className="text-[10px] font-bold text-purple-700 bg-purple-50 px-2.5 py-0.5 rounded-full border border-purple-200/60">
            {t("{count} campos selecionados", {
              count: leadFields.length + customFields.length,
            })}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
          {/* Left: Field Configuration (7/12) */}
          <div className="lg:col-span-7 flex flex-col gap-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {visibleStandardLeadFields.map((field) => {
                const active = leadFields.includes(field.id);
                return (
                  <button
                    key={field.id}
                    type="button"
                    onClick={() => {
                      if (active)
                        setLeadFields(
                          leadFields.filter((id) => id !== field.id),
                        );
                      else setLeadFields([...leadFields, field.id]);
                    }}
                    className={`flex items-center gap-2 p-2.5 rounded-xl border transition-all text-left ${
                      active
                        ? "border-purple-600 bg-purple-50/50 shadow-xs"
                        : "border-gray-200 bg-gray-50/40 hover:bg-white hover:border-purple-200"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-md flex items-center justify-center transition-all ${
                        active
                          ? "bg-purple-600 text-white"
                          : "bg-white border border-gray-300 text-transparent"
                      }`}
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                    </div>
                    <span
                      className={`text-xs font-bold truncate ${active ? "text-purple-900" : "text-gray-600"}`}
                    >
                      {t(field.label)}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Custom Questions Section */}
            <div className="pt-3 border-t border-purple-100/80 flex flex-col gap-3 flex-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Database className="w-3.5 h-3.5 text-purple-600" />
                  <span className="text-xs font-bold text-gray-900 uppercase tracking-tight">
                    {t("Perguntas Personalizadas (Opcional)")}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCustomField}
                  className="bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700 font-bold text-[10px] uppercase min-h-[32px]! h-8 px-3 rounded-lg"
                  icon={<Plus className="w-3 h-3" />}
                >
                  {t("Adicionar Pergunta")}
                </Button>
              </div>

              {customFields.length > 0 ? (
                <div className="flex-1 grid grid-cols-1 content-start">
                  {customFields.map((field) => (
                    <div
                      key={field.id}
                      className="pb-3 border-b border-gray-100 last:border-0 last:pb-0 flex items-end gap-2.5 relative"
                    >
                      <div className="flex-1 min-w-0">
                        <Input
                          label={t("Pergunta")}
                          labelRight={
                            <CharCounter
                              current={field.label.length}
                              hard={CUSTOM_QUESTION_LABEL_LIMIT}
                            />
                          }
                          value={field.label}
                          onChange={(e) =>
                            updateCustomField(field.id, {
                              label: e.target.value,
                            })
                          }
                          placeholder={t("Ex: Qual o seu cargo atual?")}
                          maxLength={CUSTOM_QUESTION_LABEL_LIMIT}
                          inputSize="sm"
                          className="text-xs rounded-xl border-purple-200"
                        />
                      </div>
                      <div className="w-40 shrink-0">
                        {platform === "linkedin" ? (
                          <div
                            className="space-y-1.5"
                            title={t(
                              "O LinkedIn não suporta tipo de pergunta customizada — todas as perguntas próprias são sempre texto livre.",
                            )}
                          >
                            <label className="text-xs font-medium text-gray-400">
                              {t("Tipo")}
                            </label>
                            <div className="h-10 flex items-center px-3.5 rounded-xl border border-dashed border-gray-200 bg-gray-50 text-xs text-gray-400">
                              {t("Texto Livre")}
                            </div>
                          </div>
                        ) : (
                          <Select
                            label={t("Tipo")}
                            value={field.type}
                            onChange={(val) =>
                              updateCustomField(field.id, { type: val })
                            }
                            options={[
                              { value: "email", label: t("E-mail") },
                              { value: "tel", label: t("Telefone") },
                              { value: "number", label: t("Numérico") },
                              { value: "text", label: t("Texto Livre") },
                            ]}
                            size="sm"
                            className="text-xs rounded-xl"
                          />
                        )}
                      </div>
                      <div className="flex items-center h-10 px-3.5 rounded-xl border border-purple-200/80 bg-white shrink-0">
                        <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-700 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) =>
                              updateCustomField(field.id, {
                                required: e.target.checked,
                              })
                            }
                            className="accent-purple-600 rounded-sm"
                          />
                          <span>{t("Obrigatório")}</span>
                        </label>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCustomField(field.id)}
                        className="w-10 h-10 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 flex items-center justify-center shrink-0 transition-colors"
                        title={t("Remover Pergunta")}
                        aria-label={t("Remover Pergunta")}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center gap-1.5 py-5 px-4 rounded-xl border border-dashed border-purple-200/70 bg-purple-50/10 text-center">
                  <Database className="w-5 h-5 text-purple-300" />
                  <p className="text-[11px] text-gray-500 font-medium">
                    {t("Nenhuma pergunta personalizada ainda")}
                  </p>
                  <button
                    type="button"
                    onClick={addCustomField}
                    className="text-[11px] font-bold text-purple-600 hover:text-purple-700 hover:underline"
                  >
                    {t("+ Adicionar a primeira pergunta")}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right: Native Form Preview (5/12) */}
          <div className="lg:col-span-5 flex flex-col">
            <div className="sticky top-4 flex flex-col gap-2 flex-1">
              <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider px-1">
                <ClipboardList className="w-3.5 h-3.5" />
                {platform === "linkedin"
                  ? t("Preview — Formulário Nativo LinkedIn")
                  : t("Preview — Formulário Instantâneo Meta")}
              </div>

              {previewFields.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center bg-gray-50/60 border border-dashed border-gray-200 rounded-xl p-6 text-center">
                  <p className="text-[11px] text-gray-400 italic">
                    {t(
                      "Selecione campos ao lado para ver o preview do formulário.",
                    )}
                  </p>
                </div>
              ) : platform === "linkedin" ? (
                <div className="flex-1 flex flex-col bg-white border border-[#0077b5]/20 rounded-xl overflow-hidden shadow-xs">
                  <div className="bg-[#0077b5] px-4 py-3">
                    <p className="text-white text-xs font-bold leading-snug">
                      {linkedinFormHeadline ||
                        t("Preencha o formulário abaixo")}
                    </p>
                    {linkedinFormDescription && (
                      <p className="text-white/80 text-[10px] mt-0.5 leading-snug">
                        {linkedinFormDescription}
                      </p>
                    )}
                  </div>
                  <div className="flex-1 flex flex-col p-4 space-y-3">
                    {previewFields.map((f) => (
                      <div key={f.id}>
                        <label className="text-[11px] font-semibold text-gray-700 flex items-center gap-1">
                          {t(f.label)}
                          {f.required && (
                            <span className="text-red-500">*</span>
                          )}
                        </label>
                        <div className="mt-1 h-9 rounded-sm border border-gray-300 bg-gray-50" />
                      </div>
                    ))}
                    <div className="flex-1" />
                    <button
                      type="button"
                      disabled
                      className="w-full h-9 rounded-full bg-[#0077b5] text-white text-xs font-bold mt-1 cursor-not-allowed"
                    >
                      {CTA_OPTIONS.find((o) => o.value === linkedinFormCta)
                        ?.label || t("Enviar")}
                    </button>
                    <p className="text-[9px] text-gray-400 leading-snug pt-1">
                      {t(
                        "Ao enviar, você concorda com a política de privacidade do anunciante.",
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col bg-white border border-[#1877f2]/20 rounded-2xl overflow-hidden shadow-xs">
                  <div className="bg-linear-to-r from-[#1877f2] to-[#0d5fc7] px-4 py-3">
                    <p className="text-white text-xs font-bold leading-snug">
                      {t("Formulário Instantâneo")}
                    </p>
                    <p className="text-white/80 text-[10px] mt-0.5 leading-snug">
                      {t("Preencha os campos abaixo para continuar")}
                    </p>
                  </div>
                  <div className="flex-1 flex flex-col p-4 space-y-3">
                    {previewFields.map((f) => (
                      <div key={f.id}>
                        <label className="text-[11px] font-semibold text-gray-700 flex items-center gap-1">
                          {t(f.label)}
                          {f.required && (
                            <span className="text-red-500">*</span>
                          )}
                        </label>
                        <div className="mt-1 h-9 rounded-lg border border-gray-200 bg-gray-50" />
                      </div>
                    ))}
                    <div className="flex-1" />
                    <button
                      type="button"
                      disabled
                      className="w-full h-9 rounded-lg bg-[#1877f2] text-white text-xs font-bold mt-1 cursor-not-allowed"
                    >
                      {t("Continuar")}
                    </button>
                    <p className="text-[9px] text-gray-400 leading-snug pt-1">
                      {t(
                        "Ao enviar, você concorda com a política de privacidade do anunciante.",
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Privacy Policy */}
        <div className="pt-3 border-t border-purple-100/80 space-y-1.5">
          <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-700 uppercase tracking-wider">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span>{t("Política de Privacidade")}</span>
            <span className="text-red-500 ml-0.5">*</span>
          </div>
          <Input
            label={t("URL da Página de Privacidade")}
            hint={t(
              "Obrigatório para exibir formulários de leads nativos (Meta e LinkedIn).",
            )}
            value={privacyPolicyUrl}
            onChange={(e) => setPrivacyPolicyUrl(e.target.value)}
            placeholder="https://seusite.com.br/privacidade"
            required
            inputSize="sm"
            className="text-xs rounded-xl border-purple-200"
          />
        </div>
      </div>

      {/* LinkedIn Lead Form Integration */}
      {platform === "linkedin" && (
        <div className="bg-white border border-[#0077b5]/20 rounded-2xl p-4 md:p-5 shadow-xs space-y-3.5">
          <div className="flex items-center justify-between border-b pb-2 border-[#0077b5]/10">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-[#0077b5] rounded-lg text-white shadow-xs">
                <ClipboardList className="w-5 h-5" />
              </div>
              <div className="h-8 flex flex-col justify-between">
                <h2 className="text-xs font-bold text-gray-900 uppercase tracking-tight leading-none">
                  {t("Formulário Nativo LinkedIn")}
                </h2>
                <span className="text-[10px] text-[#0077b5] font-medium leading-none">
                  {linkedinLeadFormUrn
                    ? `✓ ${t("Formulário Vinculado")}`
                    : t("Crie ou vincule um formulário LinkedIn")}
                </span>
              </div>
            </div>
          </div>

          {linkedinLeadFormUrn ? (
            <div className="p-3.5 bg-[#0077b5]/5 rounded-xl border border-[#0077b5]/20 flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-gray-900">
                  {linkedinFormName || t("Formulário Vinculado")}
                </p>
                <p className="text-[10px] text-gray-400 font-mono truncate max-w-xs">
                  {linkedinLeadFormUrn}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setLinkedinLeadFormUrn("");
                  setCreateError("");
                }}
                className="border-red-200 text-red-500 hover:bg-red-50 text-xs h-9 px-3 rounded-xl"
                icon={<X className="w-3 h-3" />}
              >
                {t("Trocar")}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Existing Form Selector if available */}
              {loadingForms ? (
                <div className="flex items-center gap-2 text-[11px] text-gray-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[#0077b5]" />
                  {t("Carregando formulários existentes...")}
                </div>
              ) : loadingFormsError ? (
                <div className="flex items-center gap-2 text-[11px] text-red-600 font-semibold">
                  {t("Falha ao carregar formulários existentes.")}
                  <button
                    type="button"
                    onClick={fetchLinkedinForms}
                    className="underline hover:no-underline"
                  >
                    {t("Recarregar")}
                  </button>
                </div>
              ) : (
                linkedinForms.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-gray-700 uppercase tracking-tight">
                        {t("Selecionar Formulário Existente da Conta")}
                      </span>
                      <span className="text-[10px] text-[#0077b5] font-bold">
                        {t("{count} disponíveis", {
                          count: linkedinForms.length,
                        })}
                      </span>
                    </div>
                    <Select
                      value={linkedinLeadFormUrn}
                      onChange={(val) => {
                        setLinkedinLeadFormUrn(String(val));
                        const found = linkedinForms.find(
                          (f) => f.urn === String(val),
                        );
                        if (found) setLinkedinFormName(found.name);
                      }}
                      options={[
                        {
                          value: "",
                          label: t("Selecione um formulário existente..."),
                        },
                        ...linkedinForms.map((f) => ({
                          value: f.urn,
                          label: `${f.name} (${f.status || "ACTIVE"})`,
                        })),
                      ]}
                      size="sm"
                      className="text-xs rounded-xl border-[#0077b5]/30"
                    />
                  </div>
                )
              )}

              <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider pt-1">
                {linkedinForms.length > 0
                  ? t("Ou Criar Novo Formulário via API")
                  : t("Criar Novo Formulário")}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label={t("Nome do Formulário")}
                  labelRight={
                    <CharCounter
                      current={linkedinFormName.length}
                      hard={LINKEDIN_LEAD_FORM_LIMITS.formName}
                    />
                  }
                  hint={t(
                    "Uso interno — aparece só no Campaign Manager, o lead nunca vê esse nome.",
                  )}
                  value={linkedinFormName}
                  onChange={(e) => setLinkedinFormName(e.target.value)}
                  placeholder={t("Ex: Leads - Oferta Q1")}
                  inputSize="sm"
                  className="text-xs rounded-xl border-[#0077b5]/30"
                  required
                />
                <Select
                  label={t("CTA")}
                  value={linkedinFormCta}
                  onChange={(val) => setLinkedinFormCta(val)}
                  options={CTA_OPTIONS.map((o) => ({
                    ...o,
                    label: t(o.label),
                  }))}
                  size="sm"
                  required
                  className="text-xs rounded-xl border-[#0077b5]/30"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label={t("Título (Opcional)")}
                  labelRight={
                    <CharCounter
                      current={linkedinFormHeadline.length}
                      hard={LINKEDIN_LEAD_FORM_LIMITS.headline}
                    />
                  }
                  value={linkedinFormHeadline}
                  onChange={(e) => setLinkedinFormHeadline(e.target.value)}
                  placeholder={t("Ex: Fale com nossa equipe")}
                  inputSize="sm"
                  className="text-xs rounded-xl border-[#0077b5]/30"
                />
                <Input
                  label={t("Descrição (Opcional)")}
                  labelRight={
                    <CharCounter
                      current={linkedinFormDescription.length}
                      hard={LINKEDIN_LEAD_FORM_LIMITS.description}
                    />
                  }
                  value={linkedinFormDescription}
                  onChange={(e) => setLinkedinFormDescription(e.target.value)}
                  placeholder={t(
                    "Ex: Preencha seus dados para receber o contato",
                  )}
                  inputSize="sm"
                  className="text-xs rounded-xl border-[#0077b5]/30"
                />
              </div>

              {/* Campos incluídos no formulário */}
              <div className="text-[10px] text-gray-500 bg-gray-50 rounded-lg px-2.5 py-1.5 border border-gray-100">
                <span className="font-bold text-gray-600">
                  {t("Campos incluídos:")}{" "}
                </span>
                {(leadFields.length > 0
                  ? leadFields.map((f) => f.toUpperCase())
                  : ["EMAIL", "FIRST_NAME", "LAST_NAME"]
                ).join(", ")}
                {customFields.filter((f) => f.label.trim()).length > 0 &&
                  t(" + {count} pergunta(s) personalizada(s)", {
                    count: customFields.filter((f) => f.label.trim()).length,
                  })}
              </div>

              {selectedLinkedinAccount && (
                <Button
                  type="button"
                  onClick={handleCreateFormNow}
                  disabled={
                    creating ||
                    !linkedinFormName.trim() ||
                    !privacyPolicyUrl.trim()
                  }
                  className="w-full bg-[#0077b5] hover:bg-[#005885] text-white font-bold text-xs uppercase h-9 rounded-lg shadow-xs"
                >
                  {creating ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      {t("Criando Formulário...")}
                    </span>
                  ) : (
                    t("Criar e Vincular via API")
                  )}
                </Button>
              )}

              {createError && (
                <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 space-y-1">
                  <p className="text-xs text-amber-800">{createError}</p>
                  <a
                    href="https://www.linkedin.com/campaignmanager/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] font-bold text-amber-700 hover:underline"
                  >
                    {t("Abrir LinkedIn Campaign Manager →")}
                  </a>
                </div>
              )}

              {/* Manual URN input */}
              <div
                className={`pt-2 border-t ${createError ? "border-amber-300" : "border-[#0077b5]/10"}`}
              >
                <Input
                  label={
                    createError
                      ? t(
                          "▶ Cole o URN do formulário criado no Campaign Manager",
                        )
                      : t("Ou cole o URN de formulário existente")
                  }
                  value={linkedinLeadFormUrn}
                  onChange={(e) => setLinkedinLeadFormUrn(e.target.value)}
                  placeholder="urn:li:adForm:123456789"
                  inputSize="sm"
                  className="text-xs font-mono rounded-lg border-[#0077b5]/30"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Step4_LeadForm;
